import {
  AsyncOperationProgress,
  AsyncOperationProgressAbsoluteWrapper,
  OperationProgressStatus,
  useAsyncOperationProgress,
} from "@/components/progress";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import useQueryData from "@/hooks/useQueryData";
import {
  payrollDepartmentBasedInspectionAssignmentListing,
  payrollRoleBasedInspectionAssignmentListing,
} from "@/schemas/payroll";
import { FetchListRequest, SuccessFetchListResponse } from "@/services/api";
import { PayrollsService } from "@/services/payrolls";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useCreatePayrollEntriesFromAssignmentsMutation } from "../hooks/useCreatePayrollEntriesFromAssignmentsMutation";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { usePayrollQuery } from "../hooks/usePayrollQuery";
import {
  PayrollDepartmentBasedInspectionAssignment,
  PayrollRoleBasedInspectionAssignment,
} from "../types";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { PayrollCreateEntrySubmitButton } from "./PayrollCreateEntrySubmitButton";

interface PayrollCreateEntryDialogContentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const PayrollCreateEntryDialogContent = ({
  open,
  setOpen,
}: PayrollCreateEntryDialogContentProps) => {
  const { payrollId } = usePayrollParams();
  const payrollQuery = usePayrollQuery(payrollId);
  const { store } = useRowSelectionContext();

  const isRoleBased = isProjectRoleDependent(payrollQuery.data);
  const projectDependsOn = payrollQuery.data?.project_depends_on;

  const { t } = useTranslation();

  const progress = useAsyncOperationProgress();

  const createMutation = useCreatePayrollEntriesFromAssignmentsMutation({
    payrollId,
    projectDependsOn,
    progress,
  });

  const name = "payroll-inspection-assignments";
  const options = useCreateDataTableOptions<
    | PayrollDepartmentBasedInspectionAssignment
    | PayrollRoleBasedInspectionAssignment
  >(name);
  const queryKey = useDynamicQueryParams(name, options);

  const fetchListFn = useCallback(
    (
      params: FetchListRequest<
        | PayrollDepartmentBasedInspectionAssignment
        | PayrollRoleBasedInspectionAssignment
      >,
    ) => PayrollsService.getAssignments(payrollId, projectDependsOn, params),
    [payrollId, projectDependsOn],
  );

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      fetchListFn({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [fetchListFn, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (
      lastPage: SuccessFetchListResponse<
        | PayrollDepartmentBasedInspectionAssignment
        | PayrollRoleBasedInspectionAssignment
      >,
    ) => lastPage?.last_item,
    [],
  );

  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: open && !!projectDependsOn,
  });

  const data = useQueryData(query);

  const assignmentsById = useMemo(() => {
    const result: Record<
      string,
      | PayrollRoleBasedInspectionAssignment
      | PayrollDepartmentBasedInspectionAssignment
    > = {};

    for (const assignment of data) {
      result[assignment.id] = assignment;
    }

    return result;
  }, [data]);

  const queryValue = useMemo(() => ({ query }), [query]);

  const schema = isRoleBased
    ? payrollRoleBasedInspectionAssignmentListing
    : payrollDepartmentBasedInspectionAssignmentListing;

  const handleCreate = useCallback(async () => {
    const selectedIds = Array.from(store.getSelection());
    const selectedAssignments = selectedIds
      .filter((id) => assignmentsById[id])
      .map((id) => ({
        id,
        assignment: assignmentsById[id],
        label: assignmentsById[id].contractor_name,
      }));

    await createMutation.mutateAsync(selectedAssignments);
  }, [assignmentsById, createMutation, store]);

  useEffect(() => {
    if (!open) {
      store.clear();
      progress.reset();
    }
  }, [open, store, progress]);

  const handleProgressReset = useCallback(() => {
    store.clear();
    queryClient.invalidateQueries({
      queryKey,
    });
  }, [store, queryClient, queryKey]);

  const handleProgressClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: progress.status === OperationProgressStatus.IDLE,
      enableSelectAll: progress.status === OperationProgressStatus.IDLE,
    }),
    [progress.status],
  );

  return (
    <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
      <AsyncOperationProgressAbsoluteWrapper status={progress.status}>
        <AsyncOperationProgress
          operationProgress={progress}
          onReset={handleProgressReset}
          onClose={handleProgressClose}
          translationKey="payrolls.createEntryDialog"
        />
      </AsyncOperationProgressAbsoluteWrapper>
      <DialogHeader>
        <DialogTitle>{t("payrolls.createEntryDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("payrolls.createEntryDialog.selectAssignments")}
        </DialogDescription>
      </DialogHeader>
      <div className="flex-1 overflow-hidden flex flex-col">
        <QueryTable
          schema={schema}
          name="payroll"
          query={query}
          options={options}
          translationKey="payrolls"
          tableFeatures={tableFeatures}
        >
          <QueryTable.Menubar>
            <QueryTable.Menubar.Columns inDialog />
            <QueryTable.Menubar.Reset inDialog />
          </QueryTable.Menubar>
          <DataTableQueryContextProvider value={queryValue}>
            <div className="flex flex-row grow overflow-hidden">
              <QueryTable.Content className="flex-grow min-w-0" isInDialog>
                <QueryTable.Status>
                  <QueryTable.SelectedRowsStatus />
                </QueryTable.Status>
              </QueryTable.Content>
            </div>
          </DataTableQueryContextProvider>
        </QueryTable>
      </div>
      <DialogFooter className="flex justify-between gap-2 pt-4">
        <DialogClose
          asChild
          disabled={progress.status !== OperationProgressStatus.IDLE}
        >
          <Button
            variant="secondary"
            disabled={progress.status !== OperationProgressStatus.IDLE}
          >
            {t("payrolls.createEntryDialog.cancelButton")}
          </Button>
        </DialogClose>
        <PayrollCreateEntrySubmitButton
          onConfirm={handleCreate}
          isPending={createMutation.isPending}
          progressStatus={progress.status}
        />
      </DialogFooter>
    </DialogContent>
  );
};

export const PayrollCreateEntryDialog = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <RowSelectionProvider>
        <PayrollCreateEntryDialogContent open={open} setOpen={setOpen} />
      </RowSelectionProvider>
    </Dialog>
  );
};
